import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {MatSelectSearchComponent} from "ngx-mat-select-search";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {SignalStore} from "src/store/app.store";
import {MatSnackBar} from "@angular/material/snack-bar";
import { LoaderService } from 'src/app/shared/services/loader.service';
import {toSignal} from "@angular/core/rxjs-interop";
import {forkJoin, Observable, startWith, tap} from "rxjs";
import {CountryInterface, LanguageInterface, TimezoneInterface} from "../../../../../shared/interfaces/global";
import {HttpClient} from "@angular/common/http";

@Component({
  selector: 'app-profile-settings',
    imports: [
        MatFormField,
        MatInput,
        MatLabel,
        MatOption,
        MatSelect,
        MatSelectSearchComponent,
        ReactiveFormsModule,
        TranslateModule
    ],
  templateUrl: './profile-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './profile-settings.component.scss'
})
export class ProfileSettingsComponent {
  readonly store = inject(SignalStore);
  private $toast = inject(MatSnackBar);
  public $t = inject(TranslateService);
  private $loader = inject(LoaderService);
  private $http = inject(HttpClient);

  user = this.store.getUser();
  userSettings = new FormGroup({
    name: new FormControl('', [Validators.maxLength(255)]),
    last_name: new FormControl('', [Validators.maxLength(255)]),
    country_id: new FormControl(1),
    timezone_id: new FormControl(1),
    language_id: new FormControl(1),
  });

  countryListFilter = new FormControl('', { nonNullable: true });
  timezoneListFilter = new FormControl('', { nonNullable: true });
  countryList = signal<CountryInterface[]>([]);
  timezoneList = signal<TimezoneInterface[]>([]);
  languageList = signal<LanguageInterface[]>([]);

  constructor() {
    this.loadInitialData().subscribe();
    effect(() => {
      if (this.user.id()) {
        this.userSettings.patchValue({
          name: this.user.name(),
          last_name: this.user.last_name(),
          country_id: this.user.country_id(),
          timezone_id: this.user.timezone_id(),
          language_id: this.user.language_id(),
        });
      }
    });
  }

  loadInitialData(): Observable<any> {
    return forkJoin([
      this.getCountryList(),
      this.getTimezoneList(),
      this.getLanguageList(),
    ]);
  }

  getCountryList() {
    return this.$http
      .get<{ items: CountryInterface[] }>('reference/countries')
      .pipe(
        tap((res) => {
          this.countryList.set(res.items);
          if (this.user.id()) {
            this.userSettings.controls.country_id.setValue(this.user.country_id());
          }
        }),
      );
  }

  getTimezoneList() {
    return this.$http
      .get<{ items: TimezoneInterface[] }>('reference/timezones')
      .pipe(
        tap((res) => {
          this.timezoneList.set(res.items);
          if (this.user.id()) {
            this.userSettings.controls.timezone_id.setValue(this.user.timezone_id());
          }
        }),
      );
  }

  getLanguageList() {
    return this.$http
      .get<{ items: LanguageInterface[] }>('reference/languages')
      .pipe(
        tap((res) => {
          this.languageList.set(res.items);
          if (this.user.id()) {
            this.userSettings.controls.language_id.setValue(this.user.language_id());
          }
        }),
      );
  }

  countryListFiltered = computed(() => {
    const search = (this.$countryControlsChanges() || '').toLowerCase();
    if (!search) {
      return [...this.countryList()];
    }
    return this.countryList().filter((country) =>
      country.name.toLowerCase().includes(search),
    );
  });
  $countryControlsChanges = toSignal<string>(
    this.countryListFilter.valueChanges.pipe(startWith('')),
  );

  timezoneListFiltered = computed(() => {
    const search = (this.$timezoneControlsChanges() || '').toLowerCase();
    if (!search) {
      return [...this.timezoneList()];
    }
    if (this.$t.defaultLang === 'ru')
      return this.timezoneList().filter((time) =>
        time.value.toLowerCase().includes(search),
      );
    else
      return this.timezoneList().filter((time) =>
        time.value_en.toLowerCase().includes(search),
      );
  });
  $timezoneControlsChanges = toSignal<string>(
    this.timezoneListFilter.valueChanges.pipe(startWith('')),
  );

  uploadAvatar(files: FileList | null) {
    if (!files) return;
    if (files[0].size >= 5 * 1024 * 1024) {
      this.$toast.open(this.$t.instant('error.tooLargeFile'), undefined, {
        duration: 2000,
        panelClass: ['error'],
      });
      return;
    }

    this.$loader.show();
    const data = new FormData();
    data.append('file', files[0]);

    this.store.uploadUserAvatar(data);
  }

  updateProfileSettings() {
    this.store.updateUser(this.userSettings.value);
    this.selectLang();
  }

  selectLang() {
    const lang_name = this.languageList()?.find(
      (el) => el.id === this.userSettings.controls.language_id.value,
    )?.tag;
    if (lang_name) this.$t.setDefaultLang(lang_name);
  }
}
